"use client"

import { useState } from "react"
import { CheckCircle2, Circle, ShieldCheck, FileText, Building2, Monitor, Download } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Header } from "@/components/header"
import { Footer } from "@/components/footer"

interface ChecklistItem {
  id: string
  text: string
  category: string
  subcategory: string
}

const checklistItems: ChecklistItem[] = [
  // Administrative Safeguards
  {
    id: "admin-1",
    text: "Conduct comprehensive risk analysis to identify potential risks and vulnerabilities to ePHI",
    category: "Administrative Safeguards",
    subcategory: "Security Management Process",
  },
  {
    id: "admin-2",
    text: "Document risk management policies and procedures to reduce identified risks",
    category: "Administrative Safeguards",
    subcategory: "Security Management Process",
  },
  {
    id: "admin-3",
    text: "Establish formal sanction policy for workforce members who violate security policies",
    category: "Administrative Safeguards",
    subcategory: "Security Management Process",
  },
  {
    id: "admin-4",
    text: "Regularly review information system activity logs (audit logs, access reports, security incidents)",
    category: "Administrative Safeguards",
    subcategory: "Security Management Process",
  },
  {
    id: "admin-5",
    text: "Designate a Security Officer responsible for developing and implementing security policies",
    category: "Administrative Safeguards",
    subcategory: "Assigned Security Responsibility",
  },
  {
    id: "admin-6",
    text: "Ensure workforce members have appropriate access to ePHI based on job function (least privilege)",
    category: "Administrative Safeguards",
    subcategory: "Workforce Security",
  },
  {
    id: "admin-7",
    text: "Conduct background checks or verify credentials before granting access to ePHI",
    category: "Administrative Safeguards",
    subcategory: "Workforce Security",
  },
  {
    id: "admin-8",
    text: "Implement formal procedures to terminate access when employees leave or change roles",
    category: "Administrative Safeguards",
    subcategory: "Workforce Security",
  },
  {
    id: "admin-9",
    text: "Establish policies and procedures for granting access to ePHI",
    category: "Administrative Safeguards",
    subcategory: "Information Access Management",
  },
  {
    id: "admin-10",
    text: "Provide security awareness training to all workforce members, including management",
    category: "Administrative Safeguards",
    subcategory: "Security Awareness and Training",
  },
  {
    id: "admin-11",
    text: "Train staff on recognizing and protecting against malicious software (malware, phishing)",
    category: "Administrative Safeguards",
    subcategory: "Security Awareness and Training",
  },
  {
    id: "admin-12",
    text: "Establish procedures to identify, respond to, and report security incidents",
    category: "Administrative Safeguards",
    subcategory: "Security Incident Procedures",
  },
  {
    id: "admin-13",
    text: "Document all security incidents and their outcomes",
    category: "Administrative Safeguards",
    subcategory: "Security Incident Procedures",
  },
  {
    id: "admin-14",
    text: "Implement capability to notify affected parties within 72 hours of discovering a breach",
    category: "Administrative Safeguards",
    subcategory: "Security Incident Procedures",
  },
  {
    id: "admin-15",
    text: "Document data backup plan with procedures for creating retrievable copies of ePHI",
    category: "Administrative Safeguards",
    subcategory: "Contingency Plan",
  },
  {
    id: "admin-16",
    text: "Create disaster recovery plan to restore any loss of data",
    category: "Administrative Safeguards",
    subcategory: "Contingency Plan",
  },
  {
    id: "admin-17",
    text: "Develop emergency mode operation plan for critical business processes during emergencies",
    category: "Administrative Safeguards",
    subcategory: "Contingency Plan",
  },
  {
    id: "admin-18",
    text: "Periodically test and revise contingency plans (at least annually)",
    category: "Administrative Safeguards",
    subcategory: "Contingency Plan",
  },
  {
    id: "admin-19",
    text: "Conduct periodic technical and non-technical evaluations of security program",
    category: "Administrative Safeguards",
    subcategory: "Evaluation",
  },
  {
    id: "admin-20",
    text: "Establish Business Associate Agreements (BAAs) with all vendors who handle ePHI",
    category: "Administrative Safeguards",
    subcategory: "Business Associate Contracts",
  },
  {
    id: "admin-21",
    text: "Ensure BAAs require business associates to implement appropriate safeguards",
    category: "Administrative Safeguards",
    subcategory: "Business Associate Contracts",
  },
  {
    id: "admin-22",
    text: "Monitor business associates for compliance with BAA terms",
    category: "Administrative Safeguards",
    subcategory: "Business Associate Contracts",
  },
  {
    id: "admin-23",
    text: "Designate a Privacy Officer responsible for developing and implementing privacy policies",
    category: "Administrative Safeguards",
    subcategory: "Privacy Responsibility",
  },

  // Physical Safeguards
  {
    id: "physical-1",
    text: "Establish procedures for facility access during emergency situations",
    category: "Physical Safeguards",
    subcategory: "Facility Access Controls",
  },
  {
    id: "physical-2",
    text: "Implement facility security plan to safeguard facility and equipment from unauthorized access",
    category: "Physical Safeguards",
    subcategory: "Facility Access Controls",
  },
  {
    id: "physical-3",
    text: "Control and validate access to facilities based on role or function",
    category: "Physical Safeguards",
    subcategory: "Facility Access Controls",
  },
  {
    id: "physical-4",
    text: "Document repairs and modifications to physical security components (doors, locks, walls)",
    category: "Physical Safeguards",
    subcategory: "Facility Access Controls",
  },
  {
    id: "physical-5",
    text: "Define proper functions and physical attributes of workstations accessing ePHI",
    category: "Physical Safeguards",
    subcategory: "Workstation Use",
  },
  {
    id: "physical-6",
    text: "Position workstation screens to prevent unauthorized viewing of ePHI",
    category: "Physical Safeguards",
    subcategory: "Workstation Use",
  },
  {
    id: "physical-7",
    text: "Implement physical safeguards restricting access to workstations that can access ePHI",
    category: "Physical Safeguards",
    subcategory: "Workstation Security",
  },
  {
    id: "physical-8",
    text: "Establish policies governing receipt and removal of hardware and media containing ePHI",
    category: "Physical Safeguards",
    subcategory: "Device and Media Controls",
  },
  {
    id: "physical-9",
    text: "Implement secure disposal procedures for hardware and media containing ePHI",
    category: "Physical Safeguards",
    subcategory: "Device and Media Controls",
  },
  {
    id: "physical-10",
    text: "Remove ePHI from electronic media before reuse (secure wiping)",
    category: "Physical Safeguards",
    subcategory: "Device and Media Controls",
  },
  {
    id: "physical-11",
    text: "Maintain records of hardware and electronic media movements",
    category: "Physical Safeguards",
    subcategory: "Device and Media Controls",
  },

  // Technical Safeguards
  {
    id: "technical-1",
    text: "Assign unique user identifications for identifying and tracking user identity (no shared accounts)",
    category: "Technical Safeguards",
    subcategory: "Access Control",
  },
  {
    id: "technical-2",
    text: "Establish procedures for obtaining necessary ePHI access during emergencies (break glass)",
    category: "Technical Safeguards",
    subcategory: "Access Control",
  },
  {
    id: "technical-3",
    text: "Implement automatic logoff after predetermined period of inactivity",
    category: "Technical Safeguards",
    subcategory: "Access Control",
  },
  {
    id: "technical-4",
    text: "Encrypt ePHI when stored (data at rest) - MANDATORY as of January 2025",
    category: "Technical Safeguards",
    subcategory: "Access Control",
  },
  {
    id: "technical-5",
    text: "Implement Multi-Factor Authentication (MFA) for all ePHI access - MANDATORY as of January 2025",
    category: "Technical Safeguards",
    subcategory: "Access Control",
  },
  {
    id: "technical-6",
    text: "Implement mechanisms to record and examine activity in systems containing ePHI (audit logs)",
    category: "Technical Safeguards",
    subcategory: "Audit Controls",
  },
  {
    id: "technical-7",
    text: "Retain audit logs for appropriate period (typically 6 years)",
    category: "Technical Safeguards",
    subcategory: "Audit Controls",
  },
  {
    id: "technical-8",
    text: "Regularly review audit logs for suspicious activity (at least monthly)",
    category: "Technical Safeguards",
    subcategory: "Audit Controls",
  },
  {
    id: "technical-9",
    text: "Establish policies and procedures to protect ePHI from improper alteration or destruction",
    category: "Technical Safeguards",
    subcategory: "Integrity",
  },
  {
    id: "technical-10",
    text: "Implement mechanisms to corroborate that ePHI has not been altered or destroyed",
    category: "Technical Safeguards",
    subcategory: "Integrity",
  },
  {
    id: "technical-11",
    text: "Verify that persons or entities seeking access to ePHI are who they claim to be",
    category: "Technical Safeguards",
    subcategory: "Person or Entity Authentication",
  },
  {
    id: "technical-12",
    text: "Encrypt ePHI when transmitted over electronic networks (TLS/SSL) - MANDATORY",
    category: "Technical Safeguards",
    subcategory: "Transmission Security",
  },
  {
    id: "technical-13",
    text: "Implement integrity controls to ensure ePHI is not improperly modified during transmission",
    category: "Technical Safeguards",
    subcategory: "Transmission Security",
  },
  {
    id: "technical-14",
    text: "Use secure email or encrypted messaging for sending ePHI",
    category: "Technical Safeguards",
    subcategory: "Transmission Security",
  },
  {
    id: "technical-15",
    text: "Implement network segmentation to isolate systems containing ePHI",
    category: "Technical Safeguards",
    subcategory: "Transmission Security",
  },

  // Privacy Rule Requirements
  {
    id: "privacy-1",
    text: "Provide Notice of Privacy Practices to all patients",
    category: "Privacy Rule",
    subcategory: "Individual Rights",
  },
  {
    id: "privacy-2",
    text: "Honor patient requests for access to their PHI within 30 days",
    category: "Privacy Rule",
    subcategory: "Individual Rights",
  },
  {
    id: "privacy-3",
    text: "Allow patients to request amendments to their PHI",
    category: "Privacy Rule",
    subcategory: "Individual Rights",
  },
  {
    id: "privacy-4",
    text: "Provide accounting of disclosures when requested by patients",
    category: "Privacy Rule",
    subcategory: "Individual Rights",
  },
  {
    id: "privacy-5",
    text: "Honor patient requests for confidential communications",
    category: "Privacy Rule",
    subcategory: "Individual Rights",
  },
  {
    id: "privacy-6",
    text: "Obtain patient authorization before using/disclosing PHI for marketing",
    category: "Privacy Rule",
    subcategory: "Uses and Disclosures",
  },
  {
    id: "privacy-7",
    text: "Limit use and disclosure of PHI to minimum necessary (except for treatment)",
    category: "Privacy Rule",
    subcategory: "Uses and Disclosures",
  },
  {
    id: "privacy-8",
    text: "Comply with reproductive health privacy protections (April 2024 rule)",
    category: "Privacy Rule",
    subcategory: "Uses and Disclosures",
  },
]

export default function HipaaComplianceChecklistContent() {
  const [checkedItems, setCheckedItems] = useState<Set<string>>(new Set())

  const toggleItem = (id: string) => {
    const newChecked = new Set(checkedItems)
    if (newChecked.has(id)) {
      newChecked.delete(id)
    } else {
      newChecked.add(id)
    }
    setCheckedItems(newChecked)
  }

  const categories = Array.from(new Set(checklistItems.map(item => item.category)))
  const progress = Math.round((checkedItems.size / checklistItems.length) * 100)

  const getCategoryIcon = (category: string) => {
    if (category === "Administrative Safeguards") return FileText
    if (category === "Physical Safeguards") return Building2
    if (category === "Technical Safeguards") return Monitor
    return ShieldCheck
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#0EA5E9] to-[#0284C7] text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="bg-white/20 text-white hover:bg-white/30 mb-6">
              <ShieldCheck className="mr-2 h-4 w-4" />
              Interactive Checklist
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Complete HIPAA Compliance Checklist</h1>
            <p className="text-xl text-white/90">
              Comprehensive checklist covering all Administrative, Physical, and Technical Safeguards required for HIPAA compliance. Check off items as you complete them to track your progress.
            </p>
          </div>
        </div>
      </section>

      {/* Progress Section */}
      <section className="py-8 bg-[#F0F9FF]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="border-2 border-[#0EA5E9]">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold text-[#0F172A]">Your Progress</h2>
                  <span className="text-3xl font-bold text-[#0EA5E9]">{progress}%</span>
                </div>
                <div className="w-full bg-[#E2E8F0] rounded-full h-4 mb-2">
                  <div
                    className="bg-[#0EA5E9] h-4 rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <p className="text-sm text-[#64748B]">
                  {checkedItems.size} of {checklistItems.length} items completed
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Checklist Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {categories.map((category) => {
              const categoryItems = checklistItems.filter(item => item.category === category)
              const categoryChecked = categoryItems.filter(item => checkedItems.has(item.id)).length
              const categoryProgress = Math.round((categoryChecked / categoryItems.length) * 100)
              const Icon = getCategoryIcon(category)

              return (
                <Card key={category} className="border-2 border-[#E2E8F0] mb-8">
                  <CardHeader className="bg-[#F8FAFC]">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-[#0EA5E9]/10 flex items-center justify-center">
                          <Icon className="h-6 w-6 text-[#0EA5E9]" />
                        </div>
                        <div>
                          <CardTitle className="text-xl">{category}</CardTitle>
                          <p className="text-sm text-[#64748B]">
                            {categoryChecked} of {categoryItems.length} completed
                          </p>
                        </div>
                      </div>
                      <span className="text-2xl font-bold text-[#0EA5E9]">{categoryProgress}%</span>
                    </div>
                    <div className="w-full bg-[#E2E8F0] rounded-full h-2 mt-4">
                      <div
                        className="bg-[#0EA5E9] h-2 rounded-full transition-all duration-300"
                        style={{ width: `${categoryProgress}%` }}
                      />
                    </div>
                  </CardHeader>
                  <CardContent className="pt-6">
                    <div className="space-y-4">
                      {/* Group by subcategory */}
                      {Array.from(new Set(categoryItems.map(item => item.subcategory))).map((subcategory) => {
                        const subcategoryItems = categoryItems.filter(item => item.subcategory === subcategory)
                        return (
                          <div key={subcategory} className="border-l-2 border-[#E2E8F0] pl-4">
                            <h3 className="font-bold text-[#0F172A] mb-3">{subcategory}</h3>
                            <div className="space-y-2">
                              {subcategoryItems.map((item) => (
                                <div
                                  key={item.id}
                                  className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                                    checkedItems.has(item.id)
                                      ? "bg-[#F0FDF4] border border-[#86EFAC]"
                                      : "bg-white hover:bg-[#F8FAFC] border border-[#E2E8F0]"
                                  }`}
                                  onClick={() => toggleItem(item.id)}
                                >
                                  {checkedItems.has(item.id) ? (
                                    <CheckCircle2 className="h-5 w-5 text-[#10B981] flex-shrink-0 mt-0.5" />
                                  ) : (
                                    <Circle className="h-5 w-5 text-[#94A3B8] flex-shrink-0 mt-0.5" />
                                  )}
                                  <span
                                    className={`text-sm ${
                                      checkedItems.has(item.id) ? "text-[#15803D]" : "text-[#0F172A]"
                                    }`}
                                  >
                                    {item.text}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-[#0EA5E9] to-[#0284C7]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-6">Need Help Achieving Compliance?</h2>
            <p className="text-xl text-white/90 mb-8">
              Our HIPAA compliance experts can guide you through every item on this checklist and ensure your organization is fully compliant.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/self-assessment">
                <Button size="lg" className="bg-white text-[#0EA5E9] hover:bg-white/90">
                  Take Free Assessment
                </Button>
              </a>
              <a href="/contact">
                <Button size="lg" className="bg-white/20 text-white border-2 border-white hover:bg-white hover:text-[#0EA5E9]">
                  Schedule Consultation
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}
